import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { useTracking } from 'shared/providers/TrackingProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { Route, Switch, useHistory, useRouteMatch, NavLink } from 'react-router-dom';
import { RouteDefinitions } from 'routes';
import { GraphQLFetchPolicies } from 'shared/enums';
import { CareMembershipEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';
import { PermissionType, useGetQuickSightDashboardsQuery } from 'shared/types/graphql';
import styled from 'styled-components/macro';
import { allowedReportDeepLinks } from './constants';
import PaymentReport from './PaymentReport';
import QuicksightReportIFrame from './QuickSightReports/QuicksightReportIFrame';
import QuickSightReportList from './QuickSightReports/QuickSightReportList';
import { useDisputeNotification } from 'shared/hooks/useDisputeNotification';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Flex } from '@televet/kibble-ui/build/chakra';

export const quickSightRelativePath = '/quicksight';

enum InternalReportingRoutes {
  Payments = '/payments',
}

const Reporting = (): JSX.Element => {
  const { showDisputeNotification } = useDisputeNotification();

  const { data: quickSightDashboardsData, loading: isQuickSightDashboardDataLoading } = useGetQuickSightDashboardsQuery(
    { fetchPolicy: GraphQLFetchPolicies.NetworkOnly },
  );
  const { trackEvent } = useTracking();

  const [dashboardName, setDashboardName] = useState<string>('');

  const reportingPages = useMemo(
    () => [
      {
        label: 'Payments',
        relativePath: InternalReportingRoutes.Payments,
        component: <PaymentReport />,
        permission: PermissionType.ReadOrganization,
        display: true,
        showTitle: true,
        ...(showDisputeNotification && { rightIcon: <Icon name="warningSign" variant="danger" size="md" /> }),
      },
    ],
    [showDisputeNotification],
  );

  const { path, url } = useRouteMatch();
  const history = useHistory();
  const { userHasPermission, currentClinicId } = useClinicUser();

  useEffect(() => {
    const isPaymentReport = history.location.pathname.includes(InternalReportingRoutes.Payments);
    const reportName = /[^/]*$/.exec(history.location.pathname)?.[0] || '';
    if (
      history.location.pathname === RouteDefinitions.Reporting &&
      !allowedReportDeepLinks.includes(reportName) &&
      !isPaymentReport
    ) {
      history.push(`${url}${reportingPages[0].relativePath}`);
    }
  }, [reportingPages, history, history.location, url]);

  useEffect(() => {
    const isPaymentReport = history.location.pathname.includes(InternalReportingRoutes.Payments);
    const reportName = /[^/]*$/.exec(history.location.pathname)?.[0] || '';
    if (currentClinicId && !allowedReportDeepLinks.includes(reportName) && !isPaymentReport) {
      history.push(`${url}${reportingPages[0].relativePath}`);
    }
  }, [currentClinicId, history, reportingPages, url]);

  return (
    <SettingsPageContainer>
      <NavigationSidebar>
        <NavigationSection>
          {reportingPages
            .filter(({ permission }) => !permission || userHasPermission(permission))
            .map(
              (page) =>
                page.display && (
                  <StyledLink
                    id={page.relativePath.replaceAll('/', '')}
                    to={`${url}${page.relativePath}`}
                    key={page.relativePath}
                    exact
                    data-mixpanel-name={`Reporting -- ${page.label}`}
                    onClick={(): void => {
                      if (page.label === 'Care Financial Report') {
                        trackEvent(CareMembershipEvents.FinancialReportView);
                      }
                    }}
                  >
                    {page.label}
                    {page.rightIcon && <Flex ml={2}>{page.rightIcon}</Flex>}
                  </StyledLink>
                ),
            )}

          <QuickSightReportList
            quickSightDashboardData={quickSightDashboardsData}
            isQuickSightDashboardDataLoading={isQuickSightDashboardDataLoading}
            setDashboardName={setDashboardName}
          />
        </NavigationSection>
      </NavigationSidebar>
      <Switch>
        {reportingPages.map((page) => (
          <Route path={`${path}${page.relativePath}`} key={page.relativePath}>
            <SettingsViewContainer>
              {page.showTitle && (
                <Heading size="md" px={2} py={5} borderBottom="1px" borderColor="border.default">
                  {page.label}
                </Heading>
              )}
              {page.component}
            </SettingsViewContainer>
          </Route>
        ))}

        <Route path={`${path}${quickSightRelativePath}/:dashboardURLName`}>
          <SettingsViewContainer>
            <Heading size="md" px={2} py={5} borderBottom="1px" borderColor="border.default">
              {dashboardName}
            </Heading>
            <QuicksightReportIFrame />
          </SettingsViewContainer>
        </Route>
      </Switch>
    </SettingsPageContainer>
  );
};

export default Reporting;

const SettingsPageContainer = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  height: 100%;
`;

const NavigationSidebar = styled.div`
  flex: 0 0 auto;
  width: 250px;
  border-right: 1px solid var(--chakra-colors-border-default);
  overflow: auto;
`;

const StyledLink = styled(NavLink)`
  text-decoration: none;
  display: flex;
  align-items: center;
  padding: 12px 20px;
  transition: color 0.1s ease-in-out;
  color: var(--chakra-colors-text-default);

  &:hover {
    background: var(--chakra-colors-background-primaryAlpha-hover);
  }

  &.active {
    color: var(--chakra-colors-text-brand);
    background: var(--chakra-colors-background-primaryAlpha-pressed);
  }
`;

const NavigationSection = styled.div`
  & + & {
    border-top: 1px solid var(--chakra-colors-border-default);
  }

  & h2 {
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
  }
`;

const SettingsViewContainer = styled.div`
  flex: 1 1 auto;
  height: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding: 0 20px;
`;
